/**
 * Column widths a user dragged — the day sheet's JOBS BOOKED grid and the crew grid's WORKER
 * column (13-ui-screens.md §7.5 / §7.7 [N], client request 13 Oct 2026).
 *
 * ── Two layouts, never a mixture ────────────────────────────────────────────
 * Until somebody drags, a table keeps the layout it shipped with: `width: 100%`, fixed columns
 * at their spec widths, and the one or two UNSIZED columns (PRODUCT and CUSTOMER on the jobs
 * grid, WORKER on the crew grid) stretching over whatever the window has left. The first drag
 * FREEZES that layout — every resizable column is stored at the width it was actually rendered
 * at — and from then on the table is exactly the sum of its columns, wider than the card (the
 * wrapper scrolls, §3.9) or narrower (blank to the right), never stretched to fit.
 *
 * Why freeze rather than keep a stretch column: `table-layout: fixed` shares any spare width
 * across EVERY column once none is unsized, so a column dragged to 180 would render at 230 on a
 * wide window and the drag would feel as if it did not take. Spreadsheet behaviour — one column
 * moves, nothing else does — is what a clerk expects from a header edge. The crew grid is the
 * case that asked for it: on a wide monitor its stretching WORKER column put the tick columns a
 * long way from the names they belong to.
 *
 * ── Why per browser, not per user ───────────────────────────────────────────
 * The text-size argument (`text-size.ts`): a width belongs to a person at a particular screen,
 * it moves no money and nothing on the server reads it. Every storage access is wrapped, and a
 * browser that loses it simply renders the shipped layout.
 *
 * ── Zoom ────────────────────────────────────────────────────────────────────
 * Widths are stored in CSS pixels, so they scale with the text-size setting like every other
 * width on the page. A pointer's `clientX` and an element's `getBoundingClientRect()` are VISUAL
 * pixels under the `zoom` on `<html>`, so the drag divides both by `pageZoom()` (CLAUDE.md, Web).
 */
import { useCallback, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent, RefObject } from 'react';
import { pageZoom } from './text-size';

export type ColumnWidths = Readonly<Record<string, number>>;

/** Narrow enough to tuck a column away, wide enough that its header edge can still be caught. */
export const COLUMN_WIDTH_MIN = 40;
/** A sanity ceiling for what storage hands back, not a limit anyone will drag into. */
export const COLUMN_WIDTH_MAX = 2000;

const STORAGE_PREFIX = 'cm.columnWidths.';

/** Stored text → widths, or `null` (the shipped layout) for anything unusable. Pure, for the test. */
export function parseColumnWidths(raw: string | null | undefined): ColumnWidths | null {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null;
  const out: Record<string, number> = {};
  for (const [key, value] of Object.entries(parsed)) {
    if (typeof value !== 'number' || !Number.isFinite(value)) continue;
    out[key] = clampWidth(value);
  }
  return Object.keys(out).length > 0 ? out : null;
}

export function clampWidth(width: number): number {
  return Math.min(COLUMN_WIDTH_MAX, Math.max(COLUMN_WIDTH_MIN, Math.round(width)));
}

/**
 * The width a table takes: the sum of its columns once a layout is stored and every column has a
 * number, else `null` — keep `width: 100%` and the caller's `minWidth`. A column the stored map
 * does not name (one added in a later release) falls back to its spec width; an unsized one with
 * no stored width means the freeze is incomplete, and the shipped layout is the safe answer.
 */
export function frozenTableWidth(widths: readonly (number | string | undefined)[]): number | null {
  let sum = 0;
  for (const w of widths) {
    if (typeof w !== 'number') return null;
    sum += w;
  }
  return sum;
}

function readStored(storageKey: string): ColumnWidths | null {
  try {
    return parseColumnWidths(window.localStorage.getItem(STORAGE_PREFIX + storageKey));
  } catch {
    return null;
  }
}

function writeStored(storageKey: string, widths: ColumnWidths | null): void {
  try {
    if (widths === null) window.localStorage.removeItem(STORAGE_PREFIX + storageKey);
    else window.localStorage.setItem(STORAGE_PREFIX + storageKey, JSON.stringify(widths));
  } catch {
    /* Storage refused: the widths still apply until the page is left; they are not remembered. */
  }
}

export interface ResizableColumns {
  /** The stored widths of the RESIZABLE columns, or `null` for the shipped layout. */
  widths: ColumnWidths | null;
  /** On the `<table>`. Its colgroup's `<col data-col-key>` mark the resizable columns. */
  tableRef: RefObject<HTMLTableElement | null>;
  /** On a header edge's `<ColumnResizeHandle>`. */
  startDrag: (key: string, event: ReactPointerEvent<HTMLElement>) => void;
  /** Back to the shipped layout — a handle's double-click and the card's link. */
  reset: () => void;
}

/**
 * The drag writes the DOM directly and commits to React state once, on release. A state update
 * per `pointermove` would re-render the whole jobs card — pickers, formula previews and all —
 * sixty times a second; the `<col>` and `<table>` widths are all that move, so they are set by
 * hand and React is told the result. React then writes the same values, so the two cannot
 * disagree. A press that never moves commits nothing, so a stray click or the first half of a
 * double-click cannot freeze the layout by accident.
 */
export function useResizableColumns(storageKey: string): ResizableColumns {
  const [widths, setWidths] = useState<ColumnWidths | null>(() => readStored(storageKey));
  const tableRef = useRef<HTMLTableElement | null>(null);

  const save = useCallback(
    (next: ColumnWidths | null) => {
      writeStored(storageKey, next);
      setWidths(next);
    },
    [storageKey],
  );

  const reset = useCallback(() => save(null), [save]);

  const startDrag = useCallback(
    (key: string, event: ReactPointerEvent<HTMLElement>) => {
      const table = tableRef.current;
      const headRow = table?.tHead?.rows[0];
      if (!table || !headRow || event.button !== 0) return;
      event.preventDefault();
      event.stopPropagation();

      const zoom = pageZoom();
      const cols = Array.from(table.querySelectorAll<HTMLTableColElement>(':scope > colgroup > col'));
      const cells = Array.from(headRow.cells);
      /* One header cell per column on both grids (no colspan), so index i of each is column i. */
      if (cols.length !== cells.length) return;
      const measured = cells.map((cell) => cell.getBoundingClientRect().width / zoom);
      const index = cols.findIndex((col) => col.dataset.colKey === key);
      if (index < 0) return;

      const handle = event.currentTarget;
      const startX = event.clientX;
      const pointerId = event.pointerId;
      const tableWidth = measured.reduce((a, b) => a + b, 0);
      let width = clampWidth(measured[index]!);
      let moved = false;

      function onMove(e: PointerEvent): void {
        const next = clampWidth(measured[index]! + (e.clientX - startX) / zoom);
        if (!moved) {
          if (next === width) return;
          moved = true;
          /* The freeze: every column pinned at what it rendered at, the table at their sum. */
          cols.forEach((col, i) => {
            col.style.width = `${measured[i]}px`;
          });
          table!.style.minWidth = '0px';
        }
        width = next;
        cols[index]!.style.width = `${width}px`;
        table!.style.width = `${tableWidth - measured[index]! + width}px`;
      }

      function onEnd(): void {
        handle.removeEventListener('pointermove', onMove);
        handle.removeEventListener('pointerup', onEnd);
        handle.removeEventListener('pointercancel', onEnd);
        handle.removeAttribute('data-active');
        document.body.style.removeProperty('cursor');
        document.body.style.removeProperty('user-select');
        if (handle.hasPointerCapture(pointerId)) handle.releasePointerCapture(pointerId);
        if (!moved) return;
        const next: Record<string, number> = {};
        cols.forEach((col, i) => {
          const k = col.dataset.colKey;
          if (k) next[k] = k === key ? width : clampWidth(measured[i]!);
        });
        save(next);
      }

      handle.setPointerCapture(pointerId);
      handle.setAttribute('data-active', '');
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      handle.addEventListener('pointermove', onMove);
      handle.addEventListener('pointerup', onEnd);
      handle.addEventListener('pointercancel', onEnd);
    },
    [save],
  );

  return { widths, tableRef, startDrag, reset };
}
